/** The factor library's ordering and filters - pure, so the rules are tested without a screen.
 *
 * **Nothing here knows a market or an edition by name.** Which set is the workspace's own comes
 * from `GET /factor-sets/market` (the server finds it with the call the engine prices a site
 * with); whether an edition is retired comes from the set's own `prices_new_figures`; what a
 * category is called comes from `lib/categoryLabel`. A literal `"US"` or `"uk_desnz_2026"` in this
 * file would be a second home for a fact the data already states.
 */

import type { FactorMarket } from "../api/factorEditions";
import type { FactorLibraryFactor, FactorLibrarySet } from "../api/factors";

/** How a set relates to the workspace reading the library. */
export type Relation = "prices_here" | "own_market_history" | "other_market";

export type OrderedSet = {
  set: FactorLibrarySet;
  relation: Relation;
  /** What the set prices, when `relation` is `prices_here`: sites, legal entities, or 0. */
  count: number;
};

/** The sets in the order a reader of this workspace wants them.
 *
 * 1. the set(s) that price the workspace's sites now, most sites first (the server's order);
 * 2. other editions of the same market, kept for history;
 * 3. every other market's sets, in the server's stable order.
 *
 * With no market (the read failed, or has not landed) the server's order is kept and every set is
 * `other_market`: the screen does not claim a relation it does not know.
 */
export function orderSets(
  sets: readonly FactorLibrarySet[],
  market: FactorMarket | undefined,
): OrderedSet[] {
  if (market === undefined) {
    return sets.map((set) => ({ set, relation: "other_market", count: 0 }));
  }
  const pricing = new Map(market.priced_with.map((entry, index) => [entry.factor_set_key, { index, entry }]));
  const ownJurisdictions = new Set(
    market.priced_with.flatMap((entry) => (entry.jurisdiction === null ? [] : [entry.jurisdiction])),
  );
  const classified = sets.map((set, position): OrderedSet & { rank: number; position: number } => {
    const hit = pricing.get(set.key);
    if (hit) return { set, relation: "prices_here", count: hit.entry.count, rank: hit.index, position };
    if (ownJurisdictions.has(set.jurisdiction)) {
      return { set, relation: "own_market_history", count: 0, rank: pricing.size, position };
    }
    return { set, relation: "other_market", count: 0, rank: pricing.size + 1, position };
  });
  classified.sort(
    (left, right) =>
      left.rank - right.rank ||
      Number(right.set.prices_new_figures) - Number(left.set.prices_new_figures) ||
      left.position - right.position,
  );
  return classified.map(({ set, relation, count }) => ({ set, relation, count }));
}

/** A factor labelled as carrying CO2 alone (`kgCO2`) rather than CO2 equivalent (`kgCO2e`), read
 * from the row's own label (ADR-0197: the label and the components must agree, and the server
 * refuses a row where they do not). */
export function isCo2Only(factor: Pick<FactorLibraryFactor, "unit_out">): boolean {
  return /co2$/i.test(factor.unit_out.trim());
}

/** The row's own source when it has one (the US set is two publications), else the set's - and
 * which one it was, so the screen can say a hash is the set's rather than the row's. */
export function provenanceOf(
  set: Pick<FactorLibrarySet, "published_at" | "source_url" | "source_sha256">,
  factor: Pick<FactorLibraryFactor, "source_url" | "source_published_at" | "source_sha256">,
): {
  from: "row" | "set";
  url: string | null;
  publishedAt: string | null;
  sha256: string | null;
} {
  if (factor.source_url !== null) {
    return {
      from: "row",
      url: factor.source_url,
      publishedAt: factor.source_published_at,
      sha256: factor.source_sha256,
    };
  }
  return { from: "set", url: set.source_url, publishedAt: set.published_at, sha256: set.source_sha256 };
}

export type Filters = { query: string; category: string; co2OnlyOnly: boolean };

export const NO_FILTERS: Filters = { query: "", category: "", co2OnlyOnly: false };

/** Whether a factor answers the filters. **Every word of the search must be found**,
 * case-insensitively, in what a reader can see on the row - category (its code and its customer
 * name), fuel or carrier, region, what it is per, the label it is stated in, the publisher's own id,
 * the verification row, the note, and the row's publisher and edition - or in the set's publisher,
 * label and edition (so "desnz" narrows to that publisher's rows). A value is deliberately not
 * searched: it is a string the screen only trims (rule 5), and matching digits against it would
 * invite reading "0.19" as a prefix of a number rather than as text. */
export function answers(
  set: FactorLibrarySet,
  factor: FactorLibraryFactor,
  filters: Filters,
  nameOf: (category: string) => string,
): boolean {
  if (filters.category !== "" && factor.category !== filters.category) return false;
  if (filters.co2OnlyOnly && !isCo2Only(factor)) return false;
  const words = filters.query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const haystack = [
    factor.category,
    nameOf(factor.category),
    factor.fuel_or_carrier,
    factor.region,
    factor.unit_in,
    factor.unit_out,
    isCo2Only(factor) ? "co2 only" : "",
    factor.external_id,
    factor.verification_row,
    factor.notes,
    factor.publisher,
    factor.edition,
    set.publisher,
    set.label,
    set.edition,
  ]
    .filter((part): part is string => typeof part === "string")
    .join(" ")
    .toLowerCase();
  return words.every((word) => haystack.includes(word));
}

/** Retired set to its replacement, within one jurisdiction: the pairs the screen offers to compare.
 * Exactly one current edition makes a pair; two would need a picker, which is a choice. */
export function editionPairs(
  sets: readonly FactorLibrarySet[],
): { from: FactorLibrarySet; to: FactorLibrarySet }[] {
  const pairs: { from: FactorLibrarySet; to: FactorLibrarySet }[] = [];
  const jurisdictions = [...new Set(sets.map((set) => set.jurisdiction))];
  for (const jurisdiction of jurisdictions) {
    const here = sets.filter((set) => set.jurisdiction === jurisdiction);
    const current = here.filter((set) => set.prices_new_figures);
    if (current.length !== 1) continue;
    for (const old of here.filter((set) => !set.prices_new_figures)) {
      pairs.push({ from: old, to: current[0]! });
    }
  }
  return pairs;
}

/** The categories the loaded sets hold, with how many factors each has, sorted by code - so a
 * category a deployment gains appears without a code change. */
export function categoriesIn(sets: readonly FactorLibrarySet[]): { category: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const set of sets) {
    for (const factor of set.factors) {
      counts.set(factor.category, (counts.get(factor.category) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .sort((left, right) => left[0].localeCompare(right[0]))
    .map(([category, count]) => ({ category, count }));
}
